import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../api/axiosInstance';

export const fetchMyMenu = createAsyncThunk(
  'menu/fetchMyMenu',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get('Menu/my-menu');
      return data.data;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchAllMenuItems = createAsyncThunk(
  'menu/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get('Menu');
      return data.data;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const createMenuItem = createAsyncThunk(
  'menu/create',
  async (dto, { rejectWithValue }) => {
    try {
      const { data } = await api.post('Menu', dto);
      return data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const updateMenuItem = createAsyncThunk(
  'menu/update',
  async ({ id, dto }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`Menu/${id}`, dto);
      return data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const deleteMenuItem = createAsyncThunk(
  'menu/delete',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`Menu/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const toggleMenuActive = createAsyncThunk(
  'menu/toggleActive',
  async ({ id, active }, { rejectWithValue }) => {
    try {
      await api.patch(`Menu/${id}/status?active=${active}`);
      return { id, active };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// ─── Role-based permission thunks ────────────────────────────────────────────

// Get all menu item IDs that a specific role has access to
// We derive this from the full menu list (adminMenu) using allowedRoleIds
export const fetchRoleMenuPermissions = createAsyncThunk(
  'menu/fetchRolePermissions',
  async (roleId, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await api.get('Menu');
      const allItems = data.data || [];
      // Flatten tree to get all items
      const flattenTree = (items) => {
        let result = [];
        for (const item of items) {
          result.push(item);
          if (item.children?.length) result = result.concat(flattenTree(item.children));
        }
        return result;
      };
      const flat = flattenTree(allItems);
      // Return menuItemIds where this roleId is allowed
      const allowedMenuIds = flat
        .filter(item => (item.allowedRoleIds || []).includes(roleId))
        .map(item => item.menuItemId);
      return { allItems: flat, allowedMenuIds };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Save all permissions for a role at once
// Goes through each menu item and sets/unsets the role
export const saveRoleMenuPermissions = createAsyncThunk(
  'menu/saveRolePermissions',
  async ({ roleId, checkedMenuIds, allMenuItems }, { rejectWithValue }) => {
    try {
      // For each menu item, call updatePermissions
      // We need to add/remove roleId from each item's allowedRoleIds
      const promises = allMenuItems.map(item => {
        const currentRoleIds = item.allowedRoleIds || [];
        const shouldHave = checkedMenuIds.includes(item.menuItemId);
        const hasNow = currentRoleIds.includes(roleId);

        if (shouldHave === hasNow) return null; // no change needed

        const newRoleIds = shouldHave
          ? [...currentRoleIds, roleId]
          : currentRoleIds.filter(id => id !== roleId);

        return api.post('Menu/permissions', {
          menuItemId: item.menuItemId,
          roleIds: newRoleIds,
        });
      }).filter(Boolean);

      await Promise.all(promises);
      return { roleId, checkedMenuIds };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────
const menuSlice = createSlice({
  name: 'menu',
  initialState: {
    userMenu: [],
    adminMenu: [],
    // For role permissions dialog
    flatMenuItems: [],
    rolePermissions: {},  // { [roleId]: [menuItemId, ...] }
    loading: false,
    adminLoading: false,
    permLoading: false,
    permSaving: false,
    error: null,
  },
  reducers: {
    clearMenu: (state) => {
      state.userMenu = [];
      state.adminMenu = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMyMenu.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(fetchMyMenu.fulfilled, (s, a) => { s.loading = false; s.userMenu = a.payload || []; })
      .addCase(fetchMyMenu.rejected, (s, a) => { s.loading = false; s.error = a.payload; })

      .addCase(fetchAllMenuItems.pending, (s) => { s.adminLoading = true; })
      .addCase(fetchAllMenuItems.fulfilled, (s, a) => { s.adminLoading = false; s.adminMenu = a.payload || []; })
      .addCase(fetchAllMenuItems.rejected, (s) => { s.adminLoading = false; })

      .addCase(toggleMenuActive.fulfilled, (s, a) => {
        const updateInTree = (items) => items.map(item => {
          if (item.menuItemId === a.payload.id) return { ...item, isActive: a.payload.active };
          if (item.children?.length) return { ...item, children: updateInTree(item.children) };
          return item;
        });
        s.adminMenu = updateInTree(s.adminMenu);
      })

      .addCase(fetchRoleMenuPermissions.pending, (s) => { s.permLoading = true; })
      .addCase(fetchRoleMenuPermissions.fulfilled, (s, a) => {
        s.permLoading = false;
        s.flatMenuItems = a.payload.allItems;
        // Store current role's allowed ids
        const roleId = a.meta.arg;
        s.rolePermissions[roleId] = a.payload.allowedMenuIds;
      })
      .addCase(fetchRoleMenuPermissions.rejected, (s) => { s.permLoading = false; })

      .addCase(saveRoleMenuPermissions.pending, (s) => { s.permSaving = true; })
      .addCase(saveRoleMenuPermissions.fulfilled, (s, a) => {
        s.permSaving = false;
        s.rolePermissions[a.payload.roleId] = a.payload.checkedMenuIds;
      })
      .addCase(saveRoleMenuPermissions.rejected, (s) => { s.permSaving = false; });
  },
});

export const { clearMenu } = menuSlice.actions;
export default menuSlice.reducer;